/* eslint-disable react-refresh/only-export-components */
import {
  EClaimStages,
  EClaimType,
  IBenefit,
  ICashlessTimelineItem,
  IClaimDetails,
  IMember,
} from '@/types';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTATStatus, redirectToPlumApp } from '@/utils';
import useSessionStorage from '@/hooks/useSessionStorage';
import { useAppContext } from './AppContext';
import useClaimDetails from '@/hooks/useClaimDetails';
import useMemberDetails from '@/hooks/useMemberDetails';
import useBreakpoints from '@/hooks/useBreakpoints';
import { MOBILE_DEEPLINKS } from '@/constants';

/**
 * Stats for Cashless Claim Status Page
 */
export interface IStatusTrackingStats {
  claimId: string;
  typeOfCare: string;
  insuranceProviderHandle?: string;
  tpaName?: string;
  /**
   * from utm_source in url
   */
  viewSource?: string | null;
  /**
   * if claim is just now created
   */
  isNew: boolean | null;
  stage: string;
  isStageDelayed?: boolean | null;
  isHealthCardAvailable: boolean;
  organisationId?: string;
  organisationName?: string;
  typeOfClaim: string;
}

export interface IClaimsContext {
  claimDetails: IClaimDetails;
  memberDetails: Array<IMember>;
  currentMember?: IMember;
  currentBenefit?: IBenefit;
  /**
   * whether the viewport is mobile web (tablet or mobile)
   */
  isMWeb?: boolean;
  cashless: {
    state: ECashlessUIStatus;
    isTerminal: boolean;
    rejectionReason?: string | null;
    isNew?: boolean | null;
    setShowCallbackModal?: (value: boolean) => void;
  };
  tracking: IStatusTrackingStats;
  isHealthCardAvailable: boolean;
}

const ClaimStatusContext = createContext<IClaimsContext | null>(null);

export enum ECashlessUIStatus {
  INITIATED = 'initiated',
  IN_PROCESS = 'in-process',
  SETTLED = 'settled',
  REJECTED = 'rejected',
  DISQUALIFIED = 'disqualified',
}

export const getRequestCallbackStorageKey = (claimId: string) => {
  if (!claimId) return null;
  return `${claimId}-show-callback-modal`;
};

const ClaimStatusProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { claimDetails } = useClaimDetails();
  const { memberDetails } = useMemberDetails();

  const { isMobile, isTablet } = useBreakpoints();
  const isMWeb = (isMobile || isTablet) ?? false;
  const { isWebView } = useAppContext();

  const [searchParams] = useSearchParams();
  const viewSource = searchParams.get('utm_source');
  const created = searchParams.get('created');

  const { plumClaimId: claimId } = claimDetails ?? {};

  const [showCallbackModal, setShowCallbackModal] = useSessionStorage<boolean>(
    getRequestCallbackStorageKey(claimId ?? '') ?? '',
    false,
  );

  let isNew =
    showCallbackModal === true &&
    claimDetails?.stage === EClaimStages.OPEN_LEAD;

  if (isWebView) {
    isNew = created === 'true' || isNew;
  }

  useEffect(() => {
    if (
      claimDetails?.typeOfClaim === EClaimType.REIMBURSEMENT &&
      claimId &&
      claimDetails?.typeOfBenefit !== 'health-expenses'
    ) {
      if (isWebView) {
        window?.NativeBridge?.callDeeplink?.(
          `${MOBILE_DEEPLINKS.REIMBURSEMENT_CLAIM_STATUS}${claimId}`,
        );
      } else {
        redirectToPlumApp(`/claims/${claimId}`)();
      }
    }
  }, [
    claimDetails?.typeOfClaim,
    claimDetails?.typeOfBenefit,
    claimId,
    isWebView,
  ]);

  const contextValue = useMemo(() => {
    const currentMember = memberDetails?.find((member) => {
      const isClaimForDependent =
        claimDetails?.patientId !== claimDetails?.memberId;
      if (isClaimForDependent) {
        return claimDetails?.patientId === member?.dependentId;
      }
      return claimDetails?.patientId === member?.memberId;
    });

    const currentBenefit = currentMember?.benefits?.find(
      (benefit) =>
        benefit?.orgBenefitId === claimDetails?.organisationBenefitId,
    );

    const isHealthCardAvailable = !(currentBenefit?.membership?.id == null);

    let cashlessUIState = ECashlessUIStatus.IN_PROCESS;
    let isTerminal = false;

    let currentStageFromTimelineIndex = null;
    let currentStageFromTimeline: ICashlessTimelineItem | null = null;
    let statusTrackingStats: IStatusTrackingStats = {
      claimId: claimDetails?.plumClaimId ?? '',
      isHealthCardAvailable,
      insuranceProviderHandle: currentBenefit?.plan?.provider?.handle,
      organisationId: currentMember?.organisationId,
      organisationName: currentMember?.organisationName,
      stage: claimDetails?.stage ?? '',
      tpaName: claimDetails?.tpaName,
      typeOfCare: claimDetails?.typeOfCare ?? '',
      typeOfClaim: claimDetails?.typeOfClaim ?? '',
      isStageDelayed: null,
      isNew,
      viewSource,
    };

    let cashlessRejectionReason = null;

    if (
      claimDetails?.typeOfClaim === 'Cashless' ||
      claimDetails?.typeOfClaim === 'Planned'
    ) {
      const { timeline } =
        claimDetails?.statusContent?.cashlessStatusPageContent ?? {};

      if (
        claimDetails?.stage === EClaimStages.FINAL_SETTLEMENT_REJECTED ||
        claimDetails?.stage === EClaimStages.FINAL_SETTLEMENT_REJECTED_CLOSED
      ) {
        cashlessRejectionReason =
          claimDetails?.finalSettlementRejectionReasonType;
        if (
          cashlessRejectionReason === 'Others' &&
          claimDetails?.settlementLetterURL
        ) {
          cashlessRejectionReason =
            'Refer to your rejection letter for more details';
        }
      } else if (
        claimDetails?.stage === EClaimStages.PRE_AUTH_REJECTED ||
        claimDetails?.stage === EClaimStages.PRE_AUTH_REJECTED_CLOSED
      ) {
        cashlessRejectionReason = claimDetails?.preAuthRejectionReasonType;
        if (
          cashlessRejectionReason === 'Others' &&
          claimDetails?.approvedLetterURL
        ) {
          cashlessRejectionReason =
            'Refer to your rejection letter for more details';
        }
      }

      currentStageFromTimelineIndex = timeline?.findIndex(
        (item) => item.stage === claimDetails?.stage,
      );

      if (currentStageFromTimelineIndex) {
        currentStageFromTimeline =
          timeline?.[currentStageFromTimelineIndex] ?? null;
      }

      if (currentStageFromTimeline == null) {
        if (
          claimDetails?.stage === EClaimStages.QUALIFIED_LEAD ||
          claimDetails?.stage === EClaimStages.OPEN_LEAD
        ) {
          cashlessUIState = ECashlessUIStatus.INITIATED;
        } else if (claimDetails?.stage === EClaimStages.DISQUALIFIED_LEAD) {
          cashlessUIState = ECashlessUIStatus.DISQUALIFIED;
          isTerminal = true;
        }
      } else if (currentStageFromTimeline?.terminalStage) {
        isTerminal = true;
        if (currentStageFromTimeline?.stageType === 'green') {
          cashlessUIState = ECashlessUIStatus.SETTLED;
        } else if (currentStageFromTimeline?.stageType === 'red') {
          cashlessUIState = ECashlessUIStatus.REJECTED;
        }
        if (
          currentStageFromTimeline?.stage ===
            EClaimStages.PRE_AUTH_REJECTED_CLOSED ||
          currentStageFromTimeline?.stage ===
            EClaimStages.FINAL_SETTLEMENT_REJECTED_CLOSED
        ) {
          cashlessUIState = ECashlessUIStatus.REJECTED;
        }
      }

      let isStageDelayed = null;

      if (
        currentStageFromTimelineIndex &&
        currentStageFromTimelineIndex !== -1
      ) {
        const nextStage = timeline?.[currentStageFromTimelineIndex + 1];
        isStageDelayed = getTATStatus(
          nextStage?.completionTATTime ?? '',
        )?.isExceeded;
      }

      statusTrackingStats = {
        ...statusTrackingStats,
        isStageDelayed,
      };
    }

    return {
      claimDetails,
      memberDetails,
      currentMember,
      currentBenefit,
      isMWeb,
      cashless: {
        state: cashlessUIState,
        isTerminal,
        isNew,
        setShowCallbackModal,
        rejectionReason: cashlessRejectionReason,
      },
      tracking: statusTrackingStats,
      isHealthCardAvailable,
    } as IClaimsContext;
  }, [
    claimDetails,
    memberDetails,
    isMWeb,
    isNew,
    viewSource,
    setShowCallbackModal,
  ]);

  return (
    <ClaimStatusContext.Provider value={contextValue}>
      {children}
    </ClaimStatusContext.Provider>
  );
};

const useClaimStatus = () => {
  const context = useContext(ClaimStatusContext);
  if (!context) {
    throw new Error(
      '[ClaimStatusContext] Cannot use context without wrapping it inside the Provider',
    );
  }
  return context;
};

export { useClaimStatus, ClaimStatusContext, ClaimStatusProvider };
